import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiChevronDown,
  HiArrowRightOnRectangle,
  HiBell,
  HiInbox,
  HiBars3,
  HiUserCircle,
} from 'react-icons/hi2';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';

const AluminiHeader = ({ collapsed, setCollapsed }) => {
  const { logout } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([
    { type: 'Update', message: 'Your profile has been updated', time: '2 hours ago' },
    { type: 'Event', message: 'New networking event available', time: '1 day ago' },
    { type: 'Mentor', message: 'Mentor session scheduled', time: '3 days ago' },
  ]);
  const [inboxMessages, setInboxMessages] = useState([
    { sender: 'Sarah', subject: 'Networking opportunity', time: '1 hour ago' },
    { sender: 'Mike', subject: 'Mentorship request', time: '2 hours ago' },
    { sender: 'Admin', subject: 'Welcome to alumni network', time: '1 day ago' },
  ]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(firestore, 'alumni', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const displayData = {
    firstName: userProfile?.firstname || user?.displayName?.split(' ')[0] || 'Alumni',
    lastName: userProfile?.lastname || user?.displayName?.split(' ')[1] || '',
    email: user?.email || '',
  };

  const displayName = `${displayData.firstName} ${displayData.lastName}`.trim();

  const handleLogout = async () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  if (loading) {
    return (
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-xl"
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-xl font-bold text-black">
              <span className="ml-2 text-xs font-normal text-gray-500">Alumni</span>
            </h1>
          </motion.div>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
          <div className="hidden flex-col gap-1 sm:flex">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200"></div>
            <div className="h-2 w-16 animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
      </motion.header>
    );
  }

  if (!user) return null;

  const initials =
    `${displayData.firstName.charAt(0)}${displayData.lastName.charAt(0)}`.toUpperCase();

  return (
    <header className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-4 py-3 backdrop-blur-xl md:px-6 md:py-4">
      <div className="flex items-center gap-3 md:gap-4">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="rounded-xl p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:p-2"
        >
          <HiBars3 className="h-4 w-4 md:h-5 md:w-5" />
        </button>
        <h1 className="text-lg font-bold text-black md:text-xl">
          <span className="ml-1 text-[10px] font-normal text-gray-500 md:ml-2 md:text-xs">
            Alumni
          </span>
        </h1>
      </div>

      <div className="flex items-center gap-2 md:gap-4">
        <div className="relative">
          <button
            onClick={() => setShowInbox(!showInbox)}
            className="relative rounded-xl p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:p-2"
          >
            <HiInbox className="h-4 w-4 md:h-5 md:w-5" />
            <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white md:-right-1 md:-top-1 md:h-4 md:w-4 md:text-xs">
              3
            </span>
          </button>

          {showInbox && (
            <div className="absolute right-0 mt-2 w-72 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl md:w-80">
              <div className="border-b border-gray-100 px-3 py-2 md:px-4 md:py-3">
                <h3 className="text-xs font-semibold text-black md:text-sm">Inbox</h3>
              </div>
              <div className="max-h-80 overflow-y-auto py-2 md:max-h-96">
                {inboxMessages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 px-3 py-2 md:gap-3 md:px-4 md:py-3 ${
                      index < inboxMessages.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                        <span className="text-[10px] font-semibold text-blue-600 md:text-xs">
                          {message.sender.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-black md:text-sm">{message.subject}</p>
                      <p className="mt-1 text-[10px] text-gray-500 md:text-xs">{message.time}</p>
                    </div>
                  </div>
                ))}
                {inboxMessages.length === 0 && (
                  <div className="px-3 py-2 text-center text-xs text-gray-500 md:px-4 md:py-3 md:text-sm">
                    No messages
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative rounded-xl p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:p-2"
          >
            <HiBell className="h-4 w-4 md:h-5 md:w-5" />
            <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white md:-right-1 md:-top-1 md:h-4 md:w-4 md:text-xs">
              5
            </span>
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2 w-72 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl md:w-80">
              <div className="border-b border-gray-100 px-3 py-2 md:px-4 md:py-3">
                <h3 className="text-xs font-semibold text-black md:text-sm">Notifications</h3>
              </div>
              <div className="max-h-80 overflow-y-auto py-2 md:max-h-96">
                {notifications.map((notification, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 px-3 py-2 md:gap-3 md:px-4 md:py-3 ${
                      index < notifications.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                        <span className="text-[10px] font-semibold text-green-600 md:text-xs">
                          {notification.type.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-black md:text-sm">{notification.message}</p>
                      <p className="mt-1 text-[10px] text-gray-500 md:text-xs">
                        {notification.time}
                      </p>
                    </div>
                  </div>
                ))}
                {notifications.length === 0 && (
                  <div className="px-3 py-2 text-center text-xs text-gray-500 md:px-4 md:py-3 md:text-sm">
                    No notifications
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 md:gap-3 md:px-3 md:py-2"
          >
            {loading ? (
              <div className="h-7 w-7 animate-pulse rounded-full bg-gray-200 md:h-8 md:w-8"></div>
            ) : user ? (
              <div className="relative">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white shadow-lg md:h-8 md:w-8 md:text-sm">
                  {displayData.firstName.charAt(0)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500 md:h-3 md:w-3"></div>
              </div>
            ) : (
              <HiUserCircle className="h-7 w-7 text-gray-400 md:h-8 md:w-8" />
            )}
            <div className="hidden flex-col items-start text-left md:flex">
              <span className="text-xs font-semibold text-black md:text-sm">
                {displayData.firstName}
              </span>
              <span className="text-[10px] text-gray-500 md:text-xs">{user?.email}</span>
            </div>
            <HiChevronDown className="h-3 w-3 text-gray-400 md:h-4 md:w-4" />
          </button>

          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl md:w-64">
              <div className="border-b border-gray-100 px-3 py-3 md:px-4 md:py-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white shadow-lg md:h-10 md:w-10 md:text-sm">
                    {displayData.firstName.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-black md:text-sm">
                      {displayName}
                    </div>
                    <div className="truncate text-[10px] text-gray-500 md:text-xs">
                      {user?.email}
                    </div>
                  </div>
                </div>
              </div>
              <div className="py-1 md:py-2">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-600 transition-colors hover:bg-gray-50 md:gap-3 md:px-4 md:py-3 md:text-sm"
                >
                  <HiArrowRightOnRectangle className="h-3 w-3 md:h-4 md:w-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </header>
  );
};

export default AluminiHeader;
